import React, { useState, useEffect } from "react";
import { List, Typography, Input, Button, Tooltip } from "antd";
import { UndoOutlined } from "@ant-design/icons";
import { PriceInput } from "../input-number";
import { PriceRange } from "../../data-types";

import styles from "./list-item.css";
import clsx from "clsx";
const {
  ["list-item"]: listItemClassName,
  ["list-item-range"]: listItemRangeClassName,
  ["number-input"]: numberInputClassName,
  ["price-input-container"]: priceInputContainerClassName,
  ["reset-button"]: resetButtonClassName,
  hyphen: hyphenClassName,
  ["range-inputs"]: rangeInputsClassName,
  ["title-text"]: titleTextClassName,
} = styles as any;

export type ItemProps = { title: React.ReactNode; value: string; className?: string };

export const ListItem: React.FC<ItemProps> = ({ title, value, className }) => {
  return (
    <List.Item className={clsx(listItemClassName, className && className)}>
      <Tooltip title={title} placement="topLeft">
        <div className={titleTextClassName}>
          <Typography.Text ellipsis={true}>{title}</Typography.Text>
        </div>
      </Tooltip>
      <span>{value}</span>
    </List.Item>
  );
};

export type ItemEditableProps = {
  title: React.ReactNode;
  chosen: number | null;
  calculated: number | null;
  onChangeWithDelay: (arg0: number | null) => void;
  onChange: (arg0: number | null) => void;
  currencySymbol: string;
  max?: number;
  min?: number;
};

export const CustomPriceInput: React.FC<Omit<ItemEditableProps, "title" | "onChange">> = ({
  chosen,
  calculated,
  onChangeWithDelay,
  currencySymbol,
  max,
  min,
}) => {
  const [value, setValue] = useState(chosen);

  useEffect(() => {
    setValue(chosen);
  }, [chosen]);

  return (
    <PriceInput
      currencySymbol={currencySymbol}
      size="small"
      step={100}
      max={max}
      min={min}
      className={numberInputClassName}
      value={value ?? undefined}
      placeholder={`${currencySymbol} ${calculated?.toLocaleString()}`}
      onChange={(newValue) => {
        setValue(newValue);
        onChangeWithDelay(newValue);
      }}
      onStep={(newValue, { offset, type }) => {
        if (value === null) {
          const nextValue =
            type === "up"
              ? (calculated || 0) + (offset as number)
              : (calculated || 0) - (offset as number);
          setValue(nextValue);
          onChangeWithDelay(nextValue);
        } else {
          setValue(newValue as number);
        }
      }}
    />
  );
};

export const ListItemEditable: React.FC<ItemEditableProps> = ({
  title,
  onChange,
  calculated,
  chosen,
  ...others
}) => {
  const handleReset = () => {
    onChange(null);
  };

  return (
    <List.Item className={listItemClassName}>
      <Tooltip title={title} placement="topLeft">
        <div className={titleTextClassName}>
          <Typography.Text ellipsis={true}>{title}</Typography.Text>
        </div>
      </Tooltip>
      <div className={priceInputContainerClassName}>
        {chosen !== null && chosen !== calculated && (
          <Tooltip title="Reset to calculated value from scope" placement="left">
            <Button
              type="text"
              size="small"
              shape="circle"
              icon={<UndoOutlined />}
              className={resetButtonClassName}
              onClick={handleReset}
            />
          </Tooltip>
        )}
        <CustomPriceInput calculated={calculated} chosen={chosen} {...others} />
      </div>
    </List.Item>
  );
};

export type ItemEditableRangeProps = {
  title: React.ReactNode;
  chosen: PriceRange;
  calculated: PriceRange;
  onChangeWithDelay: (arg0: Partial<PriceRange>) => void;
  onChange: (arg0: Partial<PriceRange>) => void;
  currencySymbol: string;
};
export const ListItemEditableRange: React.FC<ItemEditableRangeProps> = ({
  title,
  chosen,
  calculated,
  onChangeWithDelay,
  onChange,
  currencySymbol,
}) => {
  const handleReset = () => {
    onChange({ lower: null, upper: null });
  };

  return (
    <List.Item className={clsx(listItemClassName, listItemRangeClassName)}>
      <Tooltip title={title} placement="topLeft">
        <div className={titleTextClassName}>
          <Typography.Text ellipsis={true}>{title}</Typography.Text>
        </div>
      </Tooltip>
      <div className={priceInputContainerClassName}>
        {((chosen.upper !== null && chosen.upper !== calculated.upper) ||
          (chosen.lower !== null && chosen.lower !== calculated.lower)) && (
          <Tooltip title="Reset to calculated value from scope" placement="left">
            <Button
              type="text"
              size="small"
              shape="circle"
              icon={<UndoOutlined />}
              className={resetButtonClassName}
              onClick={handleReset}
            />
          </Tooltip>
        )}
        <div className={rangeInputsClassName}>
          <CustomPriceInput
            currencySymbol={currencySymbol}
            chosen={chosen.lower}
            calculated={calculated.lower}
            max={chosen.upper ?? undefined}
            onChangeWithDelay={(value) => onChangeWithDelay({ lower: value })}
          />
          <Input size="small" className={hyphenClassName} placeholder="-" disabled />
          <CustomPriceInput
            currencySymbol={currencySymbol}
            chosen={chosen.upper}
            calculated={calculated.upper}
            min={chosen.lower ?? undefined}
            onChangeWithDelay={(value) => onChangeWithDelay({ upper: value })}
          />
        </div>
      </div>
    </List.Item>
  );
};
